import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Layout } from "../components/layout/Layout";
import { Section, SectionHeader } from "../components/common/Section";
import { FaCogs, FaLaptopCode, FaMicrochip, FaTools, FaCheckCircle, FaArrowRight } from "react-icons/fa";

const Manufacturing = () => {
  const capabilities = [
    {
      icon: FaCogs,
      title: "Cabinet Manufacturing",
      description: "Custom-built gaming cabinets crafted with premium materials, precision engineering, and attention to every detail.",
      features: [
        "Premium grade steel and wood construction",
        "Custom dimensions and configurations",
        "Ergonomic design for player comfort",
        "Durable powder-coated finishes",
      ],
    },
    {
      icon: FaLaptopCode,
      title: "Software Development",
      description: "In-house development of cutting-edge gaming software that delivers engaging experiences and reliable performance.",
      features: [
        "Custom game development",
        "User interface design",
        "Backend systems integration",
        "Regular updates and support",
      ],
    },
    {
      icon: FaMicrochip,
      title: "Electronics Integration",
      description: "State-of-the-art electronic components and systems integration for seamless gaming experiences.",
      features: [
        "High-quality displays and monitors",
        "Responsive touch controls",
        "Advanced audio systems",
        "Network connectivity",
      ],
    },
    {
      icon: FaTools,
      title: "Quality Assurance",
      description: "Rigorous testing protocols ensure every product meets our premium standards before deployment.",
      features: [
        "Comprehensive functionality testing",
        "Durability and stress testing",
        "Software validation",
        "Pre-deployment certification",
      ],
    },
  ];

  const stats = [
    { value: "500+", label: "Cabinets Built" },
    { value: "50+", label: "Game Titles" },
    { value: "100%", label: "Quality Tested" },
    { value: "24/7", label: "Support Available" },
  ];

  return (
    <Layout>
      <Helmet>
        <title>Skill Game Manufacturing | Custom Gaming Cabinets & Software | Crown Amusement</title>
        <meta name="description" content="Crown Amusement manufacturing capabilities: custom skill game cabinet fabrication, gaming software development, electronic assembly, and quality assurance. We build premium gaming solutions from concept to deployment." />
        <link rel="canonical" href="https://crownamusement.com/manufacturing" />
        <meta property="og:title" content="Skill Game Manufacturing | Crown Amusement" />
        <meta property="og:description" content="Custom gaming cabinet fabrication, software development, and electronic assembly for skill games." />
        <meta property="og:url" content="https://crownamusement.com/manufacturing" />
      </Helmet>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="manufacturing-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1758260759878-f58196990628?crop=entropy&cs=srgb&fm=webp&w=600&h=400&fit=crop&q=50"
            alt="Skill game manufacturing facility with circuit boards and assembly"
            className="w-full h-full object-cover opacity-40"
            width="600"
            height="400"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/95 to-obsidian" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1.5 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full mb-6 tracking-wider uppercase border border-gold-500/20">
              Manufacturing Excellence
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white">
              Crafting the Future of{" "}
              <span className="text-gold-gradient">Gaming</span>
            </h1>
            <p className="mt-6 text-gray-300 text-lg leading-relaxed">
              Our state-of-the-art facility combines traditional craftsmanship with modern technology, 
              producing gaming cabinets that set industry standards for quality and performance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <Section background="surface">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="stats-grid">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="font-heading text-4xl md:text-5xl font-bold text-gold-500">
                {stat.value}
              </div>
              <div className="mt-2 text-gray-400 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Capabilities */}
      <Section background="default">
        <SectionHeader
          label="Our Capabilities"
          title="End-to-End Manufacturing"
          description="From initial design to final deployment, we handle every aspect of gaming machine production."
        />

        <div className="space-y-16">
          {capabilities.map((capability, index) => (
            <motion.div
              key={capability.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className={`flex flex-col ${
                index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
              } gap-12 items-center`}
              data-testid={`capability-${index}`}
            >
              {/* Icon/Visual */}
              <div className="lg:w-1/2">
                <div className="relative p-12 rounded-3xl bg-gradient-to-br from-surface to-charcoal border border-white/5">
                  <div className="absolute inset-0 bg-gaming-glow opacity-30 rounded-3xl" />
                  <div className="relative flex items-center justify-center">
                    <div className="w-32 h-32 rounded-full bg-gold-500/10 flex items-center justify-center">
                      <capability.icon className="text-6xl text-gold-500" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="lg:w-1/2">
                <h3 className="font-heading text-3xl text-white mb-4">{capability.title}</h3>
                <p className="text-gray-400 leading-relaxed mb-6">{capability.description}</p>
                <ul className="space-y-3">
                  {capability.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3 text-gray-300">
                      <FaCheckCircle className="text-gold-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Process */}
      <Section background="gradient">
        <SectionHeader
          label="Our Process"
          title="From Concept to Reality"
          description="A streamlined manufacturing process that ensures quality at every step."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { step: "01", title: "Design", desc: "Custom designs tailored to your specifications" },
            { step: "02", title: "Fabrication", desc: "Precision manufacturing with premium materials" },
            { step: "03", title: "Integration", desc: "Software and hardware integration and testing" },
            { step: "04", title: "Delivery", desc: "Quality inspection and secure delivery" },
          ].map((item, index) => (
            <motion.div
              key={item.step}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="relative p-8 rounded-2xl bg-white/5 border border-white/10"
              data-testid={`process-step-${index}`}
            >
              <span className="font-accent text-5xl text-gold-500/20 font-bold">
                {item.step}
              </span>
              <h4 className="font-heading text-xl text-white mt-4">{item.title}</h4>
              <p className="text-gray-400 text-sm mt-2">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section background="surface">
        <div className="text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl text-white font-semibold"
          >
            Ready to Start Your Project?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="mt-6 text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Let&apos;s discuss your gaming machine requirements and create something exceptional together.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-10"
          >
            <Link
              to="/contact"
              data-testid="manufacturing-contact-cta"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian font-bold rounded-full hover:scale-105 transition-transform shadow-gold-glow"
            >
              Get in Touch
              <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </Section>
    </Layout>
  );
};

export default Manufacturing;
